import React from 'react';
import { useSelector } from 'react-redux';
import PlatformBadge from '../components/platform/PlatformBadge';
import PlatformButton from '../components/platform/PlatformButton';
import PlatformIcon from '../components/platform/PlatformIcon';
import '../styles/platform-dashboard.css';

const INCLUDED_USERS = 10;
const OVERAGE_PER_USER = 39;

const MONTHLY_PRICE = 499;
const ANNUAL_PRICE = 4788;
const ANNUAL_EFFECTIVE_MONTHLY = Math.round(ANNUAL_PRICE / 12);

const MONTHLY_FEATURES = [
  'AI-drafted nurture touches',
  'Contact rotation & approve-to-send workflow',
  'Greeting cards & newsletter broadcasts',
  'Email reminders for pending touches',
  'Org admin dashboard & user management',
  'Company invite link for new users',
  `Up to ${INCLUDED_USERS} nurturer users included`,
  `Additional users at $${OVERAGE_PER_USER}/user/mo`,
  'Unlimited contacts',
  'Standard email support',
];

const ANNUAL_FEATURES = [
  'Everything in the Monthly plan',
  'Unlimited AI touches & newsletters',
  'Advanced org analytics (12-month history)',
  'CSV export for contacts & activity',
  'Custom templates & branded outbound emails',
  'Smart “going cold” contact alerts',
  `Up to ${INCLUDED_USERS} nurturer users included`,
  `Additional users at $${OVERAGE_PER_USER}/user/mo`,
  'Priority support & concierge onboarding call',
  `Save 20% vs monthly (~$${ANNUAL_EFFECTIVE_MONTHLY}/mo)`,
];

function PlanFeature({ text }) {
  return (
    <div className="platform-row subscription-feature">
      <PlatformIcon name="check" size={16} className="subscription-feature-icon" />
      <span>{text}</span>
    </div>
  );
}

function SubscriptionPlan({
  name,
  price,
  period,
  priceNote,
  features,
  badge,
  featured = false,
  actionLabel,
  actionVariant = 'outline',
  disabled = false,
  onAction,
}) {
  return (
    <div className={`platform-card subscription-plan ${featured ? 'subscription-plan--featured' : ''}`}>
      <div className="platform-row subscription-plan-head">
        <h3 className="subscription-plan-name">{name}</h3>
        {badge && <PlatformBadge tone={badge.tone}>{badge.label}</PlatformBadge>}
      </div>
      <div>
        <p className="subscription-plan-price">
          {price}
          <span className="subscription-plan-period">/{period}</span>
        </p>
        {priceNote && <p className="subscription-plan-note">{priceNote}</p>}
      </div>
      <div className="subscription-feature-list">
        {features.map((feature) => (
          <PlanFeature key={feature} text={feature} />
        ))}
      </div>
      <div className="subscription-actions">
        <PlatformButton
          variant={actionVariant}
          size="md"
          disabled={disabled}
          onClick={onAction}
          style={{ width: '100%' }}
        >
          {actionLabel}
        </PlatformButton>
      </div>
    </div>
  );
}

const SubscriptionPage = () => {
  const { company } = useSelector((state) => state.auth || {});
  const subscription = company?.subscription?.[0];
  const hasSubscription = Boolean(company?.subscription?.length);

  return (
    <div className="nurturer-platform">
      <div className="subscription-view platform-fade">
        {hasSubscription && (
          <div className="platform-card subscription-current">
            <div className="subscription-current-label">Current plan</div>
            <h3 className="subscription-current-title">Annual plan</h3>
            <p className="subscription-current-meta">
              Next billing: {subscription?.expiration || '—'}
              <br />
              Payment method: {subscription?.paymentMethod || '—'}
            </p>
          </div>
        )}

        <div className="subscription-plans">
          <SubscriptionPlan
            name="Monthly plan"
            price={`$${MONTHLY_PRICE.toLocaleString()}`}
            period="month"
            priceNote={`Includes up to ${INCLUDED_USERS} nurturer users`}
            features={MONTHLY_FEATURES}
            badge={{ tone: 'accent', label: 'Flexible billing' }}
            actionLabel={hasSubscription ? 'Change to monthly' : 'Subscribe monthly'}
            actionVariant="outline"
          />

          <SubscriptionPlan
            name="Annual plan"
            price={`$${ANNUAL_PRICE.toLocaleString()}`}
            period="year"
            priceNote={`≈ $${ANNUAL_EFFECTIVE_MONTHLY}/mo · includes up to ${INCLUDED_USERS} nurturer users`}
            features={ANNUAL_FEATURES}
            badge={hasSubscription ? { tone: 'sent', label: 'Current plan' } : { tone: 'primary', label: 'Best value' }}
            featured={hasSubscription}
            actionLabel={hasSubscription ? 'Current plan' : 'Subscribe annually'}
            actionVariant={hasSubscription ? 'soft' : 'primary'}
            disabled={hasSubscription}
          />
        </div>

        <div className="platform-card subscription-overage">
          <div className="subscription-overage-title">Need more than {INCLUDED_USERS} users?</div>
          <p className="subscription-overage-copy">
            Both plans include up to {INCLUDED_USERS} nurturer users and unlimited contacts.
            Additional users are billed at{' '}
            <strong>${OVERAGE_PER_USER}/user/mo</strong> beyond your included seats.
          </p>
        </div>

        {hasSubscription && (
          <>
            <div className="platform-row subscription-note">
              <PlatformIcon name="gift" size={18} />
              <span>
                Your subscription renews automatically on{' '}
                <strong>{subscription?.expiration || 'the next billing date'}</strong>.
              </span>
            </div>

            <div className="subscription-actions" style={{ maxWidth: 360, marginTop: 18 }}>
              <PlatformButton variant="outline" size="md" style={{ width: '100%' }}>
                Change payment method
              </PlatformButton>
              <PlatformButton variant="ghost" size="md" style={{ width: '100%', color: 'var(--muted)' }}>
                Cancel subscription
              </PlatformButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
